
import React, { useEffect, useState } from "react";
import tinycolor from "tinycolor2";
import { useTheme } from "../CommonComponents/ThemeProvider";
import PrimaryButton from "../CommonComponents/PrimaryButton";

export default function AppearanceSection() {
  const { themeId, setThemeId, themes, currentTheme } = useTheme();
  const [selectedTheme, setSelectedTheme] = useState(themeId);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Find the theme object for preview
  const previewTheme = themes.find(t => t.id === selectedTheme) || currentTheme;

  useEffect(() => {
    setSelectedTheme(themeId);
    setShowConfirmation(false);
  }, [themeId]);

  const handleThemeChange = (theme) => {
    setSelectedTheme(theme.id);
    setShowConfirmation(theme.id !== themeId);
  };

  const handleConfirm = () => {
    setThemeId(selectedTheme);
    setShowConfirmation(false);
  };

  const handleCancel = () => {
    setSelectedTheme(themeId);
    setShowConfirmation(false);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-5 pb-6 pr-6 [scrollbar-gutter:stable]">
        <div className="flex flex-col gap-6">
          <div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">Select Theme</p>
                <p className="mt-1 text-sm text-slate-500">Choose the accent color used across navigation, buttons, and table headers.</p>
              </div>
              <div
                className="inline-flex w-fit shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold"
                style={{
                  borderColor: tinycolor(previewTheme.color).setAlpha(0.25).toRgbString(),
                  backgroundColor: tinycolor(previewTheme.color).setAlpha(0.08).toRgbString(),
                  color: previewTheme.color,
                }}
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: previewTheme.color }} />
                {previewTheme.name}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {themes.map((theme) => {
              const isSelected = selectedTheme === theme.id;
              const isApplied = themeId === theme.id;
              const tint = tinycolor(theme.color).setAlpha(0.09).toRgbString();
              const border = tinycolor(theme.color).setAlpha(isSelected ? 0.55 : 0.18).toRgbString();
              const dark = tinycolor(theme.color).darken(12).toString();

              return (
                <button
                  key={theme.id}
                  type="button"
                  onClick={() => handleThemeChange(theme)}
                  className="group flex items-center justify-between rounded-2xl border bg-white p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none"
                  style={{
                    borderColor: border,
                    background: isSelected ? `linear-gradient(135deg, ${tint}, #fff)` : "#fff",
                    boxShadow: isSelected ? `0 14px 30px ${tinycolor(theme.color).setAlpha(0.16).toRgbString()}` : undefined,
                  }}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div
                      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border-4 border-white shadow-md"
                      style={{ background: `linear-gradient(135deg, ${theme.color}, ${dark})` }}
                    >
                      {isSelected && <span className="h-3 w-3 rounded-full bg-white shadow-sm" />}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">{theme.name}</p>
                      <p className="mt-1 text-xs text-slate-500">{isApplied ? "Currently applied" : isSelected ? "Ready to apply" : "Preview theme"}</p>
                    </div>
                  </div>
                  <div
                    className="h-5 w-5 shrink-0 rounded-full border-2 transition"
                    style={{
                      borderColor: isSelected ? theme.color : "#cbd5e1",
                      backgroundColor: isSelected ? theme.color : "#fff",
                      boxShadow: isSelected ? `inset 0 0 0 4px #fff` : "none",
                    }}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="shrink-0 flex flex-col gap-2 border-t border-gray-100 bg-slate-50/95 px-5 pb-5 pt-3 shadow-[0_-18px_36px_rgba(15,23,42,0.06)] backdrop-blur min-[360px]:flex-row sm:pb-7 sm:pt-4">
        <PrimaryButton
          onClick={handleConfirm}
          disabled={!showConfirmation}
          className="flex-1 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          style={{ background: previewTheme.color }}
        >
          Apply Theme
        </PrimaryButton>
        <button
          type="button"
          onClick={handleCancel}
          disabled={!showConfirmation}
          className="flex-1 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
